import React, { useEffect, useState } from "react";
import QRCode from "qrcode";
import { toast } from "react-toastify";
import { Copy, Download } from "lucide-react";
import Button from "components/button";
import UseContext from "hooks/useContext";
import { toInviteQrCodeFileName } from "components/privatePool/privatePoolContact";

const PREVIEW_SIZE_PX = 80;
const DOWNLOAD_SIZE_PX = 1024;
const INLINE_BUTTON_CLASS = "h-9 shrink-0 px-3 whitespace-nowrap";

/**
 * Grey block of the add modal: the company's invite link as a QR code preview, with
 * "Copier le lien" and "QR code" (PNG download). Neither button touches the form nor closes the modal.
 */
const PrivatePoolInviteLink = ({ url, companyName }) => {
    const { language } = UseContext();
    const label = (key, defaultValue) => language.getValue(`privatePool.addModal.${key}`, defaultValue);
    const [previewSrc, setPreviewSrc] = useState("");

    useEffect(() => {
        if (!url) return undefined;
        let isCurrent = true;
        // Twice the displayed size, so the preview stays sharp on a retina screen.
        QRCode.toDataURL(url, { width: PREVIEW_SIZE_PX * 2, margin: 0 })
            .then((dataUrl) => {
                if (isCurrent) setPreviewSrc(dataUrl);
            })
            .catch(() => {
                if (isCurrent) setPreviewSrc("");
            });
        return () => {
            isCurrent = false;
        };
    }, [url]);

    const copyLink = () => {
        if (!url) return;
        navigator.clipboard.writeText(url).then(
            () => toast.success(label("linkCopied", "Lien copié")),
            () => toast.error(label("linkCopyError", "Le lien n'a pas pu être copié"))
        );
    };

    const downloadQrCode = () => {
        if (!url) return;
        // A margin around the code: scanners need a quiet zone once it is printed.
        QRCode.toDataURL(url, { width: DOWNLOAD_SIZE_PX, margin: 2 })
            .then((dataUrl) => {
                const link = document.createElement("a");
                link.href = dataUrl;
                link.download = toInviteQrCodeFileName(companyName);
                document.body.appendChild(link);
                link.click();
                link.remove();
            })
            .catch(() => toast.error(label("qrCodeError", "Le QR code n'a pas pu être généré")));
    };

    return (
        <div className="flex items-center gap-3 rounded-lg bg-gray-50 p-4">
            <div
                className="flex shrink-0 items-center justify-center bg-white"
                style={{ width: PREVIEW_SIZE_PX, height: PREVIEW_SIZE_PX }}
            >
                {previewSrc ? (
                    <img
                        src={previewSrc}
                        width={PREVIEW_SIZE_PX}
                        height={PREVIEW_SIZE_PX}
                        alt={label("qrCodeAlt", "QR code du lien d'invitation")}
                    />
                ) : (
                    <div className="h-full w-full animate-pulse rounded bg-gray-200" aria-hidden="true" />
                )}
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-2">
                <div className="flex flex-col gap-0.5">
                    <p className="ft-sm font-medium text-gray-900">{label("selfSignupTitle", "Laissez-le s'inscrire seul")}</p>
                    <p className="ft-xs text-gray-500">
                        {label(
                            "selfSignupDescription",
                            "Partagez le lien ou affichez le QR code : il rejoint votre vivier en remplissant un court formulaire."
                        )}
                    </p>
                </div>
                {/* Side by side in the compact modal (4l): tighter padding than a SM button, no wrapping. */}
                <div className="flex gap-2">
                    <Button size="SMI" color="SECONDARY" className={INLINE_BUTTON_CLASS} disabled={!url} onClick={copyLink}>
                        <Copy size={16} aria-hidden="true" />
                        {label("copyLink", "Copier le lien")}
                    </Button>
                    <Button size="SMI" color="SECONDARY" className={INLINE_BUTTON_CLASS} disabled={!url} onClick={downloadQrCode}>
                        <Download size={16} aria-hidden="true" />
                        {label("downloadQrCode", "QR code")}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default PrivatePoolInviteLink;
